import { cn } from '@/lib/utils';

export const SURVEY_DETAIL_TABS = ['questions', 'results'] as const;
export type SurveyDetailTab = (typeof SURVEY_DETAIL_TABS)[number];

/** `?tab=` of the survey page: anything else is the Questions tab. */
export function resolveSurveyDetailTab(value: unknown): SurveyDetailTab {
  return value === 'results' ? 'results' : 'questions';
}

const TAB_CLASS =
  'inline-flex min-h-11 items-center rounded-[var(--r-pill)] border px-5 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clovie-brand focus-visible:ring-offset-2';
const IDLE_CLASS = 'border-clovie-border-soft bg-clovie-surface text-clovie-text hover:bg-clovie-hover';
const ACTIVE_CLASS = 'border-clovie-primary bg-clovie-primary text-white hover:bg-clovie-primary';

/**
 * Questions · Results — one pill each, like the Reviews tabs. Switching only changes `?tab=` on the same page, so the
 * unsaved-changes guard never fires and the builder's edits stay. Results shows the answer count.
 */
export function SurveyDetailTabs({
  tab,
  answeredCount,
  onSelect,
}: {
  tab: SurveyDetailTab;
  answeredCount: number;
  onSelect: (tab: SurveyDetailTab) => void;
}) {
  const pill = (target: SurveyDetailTab, label: string) => (
    <button
      type="button"
      onClick={() => onSelect(target)}
      aria-current={tab === target ? 'page' : undefined}
      className={cn(TAB_CLASS, tab === target ? ACTIVE_CLASS : IDLE_CLASS)}
    >
      {label}
    </button>
  );
  return (
    <nav aria-label="Survey sections" className="mb-6 flex flex-wrap gap-2">
      {pill('questions', 'Questions')}
      {pill('results', answeredCount > 0 ? `Results · ${answeredCount}` : 'Results')}
    </nav>
  );
}
